import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Search } from 'lucide-react';
import { useDailyStockCounts } from '@/hooks/useInventory';
import { format } from 'date-fns';
import { DateNavigator } from './DateNavigator';

export function StockCountHistory() {
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState<string>(format(new Date(), 'yyyy-MM-dd'));

  const { data: recentCounts, isLoading } = useDailyStockCounts({
    startDate: dateFilter,
    endDate: dateFilter,
  });

  const filteredCounts = recentCounts?.filter((c) =>
    c.product?.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.variance_reason?.toLowerCase().includes(search.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4">
        <DateNavigator date={dateFilter} onChange={setDateFilter} />
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search counts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Product</TableHead>
              <TableHead className="text-right">System Qty</TableHead>
              <TableHead className="text-right">Physical Qty</TableHead>
              <TableHead className="text-right">Variance</TableHead>
              <TableHead>Reason</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredCounts?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                  No stock counts found
                </TableCell>
              </TableRow>
            ) : (
              filteredCounts?.map((count) => (
                <TableRow key={count.id}>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {format(new Date(count.count_date), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell className="font-medium">
                    {count.product?.name || 'Unknown'}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {count.system_quantity.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {count.physical_quantity.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge
                      variant={
                        count.variance > 0
                          ? 'default'
                          : count.variance < 0
                          ? 'destructive'
                          : 'secondary'
                      }
                    >
                      {count.variance > 0 ? '+' : ''}
                      {count.variance.toLocaleString()}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-[200px] truncate text-muted-foreground">
                    {count.variance_reason || '-'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
